import type { UserCreate } from '../schemas/user.schema';
import type { CurrentUserResponse, LoginRequest, LoginResponse } from '../types/auth.types';
import { 
  login as rpcLogin,
  logout as rpcLogout,
  getCurrentUser as rpcGetCurrentUser,
  register as rpcRegister,
  changePassword as rpcChangePassword,
  hasPermission as rpcHasPermission,
} from './rpc';
import logger from '@platform/common/helpers/logger';

/**
 * AuthController — контроллер авторизации по паттерну Controller + useSyncExternalStore.
 *
 * - Держит собственное состояние (вне React) и набор подписчиков
 * - Предоставляет стабильный снапшот состояния для корректной работы с useSyncExternalStore
 * - Обновляет состояние НАПРЯМУЮ через RPC (login/logout/getCurrentUser/register/changePassword),
 *   без участия каких‑либо легаси-слоёв
 * - Предназначен для использования совместно с хуком `useAuthController(selector?)`
 */
export interface AuthControllerState {
  isAuthenticated: boolean;
  user: NonNullable<CurrentUserResponse['user']> | null;
  isLoading: boolean;
  error: string | null;
}

export class AuthController {
  private static instance: AuthController;
  private listeners = new Set<() => void>();
  private state: AuthControllerState = {
    isAuthenticated: false,
    user: null,
    isLoading: false,
    error: null,
  };

  private constructor() {
    // Инициализируем состояние из RPC при создании контроллера
    void this.initializeAuth();
  }

  /** Получение singleton-инстанса контроллера */
  static getInstance(): AuthController {
    if (!AuthController.instance) AuthController.instance = new AuthController();
    return AuthController.instance;
  }

  /** Подписка на изменения состояния */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Стабильный снапшот для useSyncExternalStore */
  getSnapshot = (): AuthControllerState => this.state;

  private emit(): void {
    for (const l of this.listeners) l();
  }

  /**
   * Заменяет состояние на новое, эмитит изменения только при реальной смене.
   */
  private replaceState(next: AuthControllerState): void {
    try {
      if (JSON.stringify(this.state) === JSON.stringify(next)) return;
    } catch {
      /* noop */
    }
    this.state = next;
    this.emit();
  }

  /** Инициализация состояния авторизации (первичная загрузка текущего пользователя) */
  private async initializeAuth(): Promise<void> {
    try {
      this.replaceState({ ...this.state, isLoading: true, error: null });
      const resp = await rpcGetCurrentUser();
      this.replaceState({
        ...this.state,
        isAuthenticated: Boolean(resp.authenticated && resp.user),
        user: resp.user ?? null,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      logger.error('[AuthController] initializeAuth error:', error);
      this.replaceState({
        ...this.state,
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: 'Failed to initialize authentication',
      });
    }
  }

  // ===== Экшены (напрямую через RPC) =====

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      this.replaceState({ ...this.state, isLoading: true, error: null });
      const res = await rpcLogin(credentials);
      if (res.success && res.user) {
        this.replaceState({
          ...this.state,
          isAuthenticated: true,
          user: res.user,
          isLoading: false,
          error: null,
        });
      } else {
        this.replaceState({ ...this.state, isAuthenticated: false, user: null, isLoading: false, error: res.error || 'Login failed' });
      }
      return res;
    } catch (error) {
      logger.error('[AuthController] login error:', error);
      this.replaceState({ ...this.state, isAuthenticated: false, user: null, isLoading: false, error: 'Login failed' });
      return { success: false, error: 'Login failed' };
    }
  }

  async logout(): Promise<void> {
    try {
      this.replaceState({ ...this.state, isLoading: true });
      // Завершаем обе сессии: Express (local) и OAuth (federated)
      await Promise.allSettled([
        rpcLogout(),
        fetch('/api/identity/v1/auth/oauth/logout', { method: 'POST' }),
      ]);
      this.replaceState({ ...this.state, isAuthenticated: false, user: null, isLoading: false, error: null });
    } catch (error) {
      logger.error('[AuthController] logout error:', error);
      this.replaceState({ ...this.state, isAuthenticated: false, user: null, isLoading: false, error: null });
    }
  }

  async register(userData: UserCreate): Promise<LoginResponse> {
    try {
      this.replaceState({ ...this.state, isLoading: true, error: null });
      const res = await rpcRegister(userData);
      if (res.success && res.user) {
        this.replaceState({ ...this.state, isAuthenticated: true, user: res.user, isLoading: false, error: null });
      } else {
        this.replaceState({ ...this.state, isLoading: false, error: res.error || 'Registration failed' });
      }
      return res;
    } catch (error) {
      logger.error('[AuthController] register error:', error);
      this.replaceState({ ...this.state, isLoading: false, error: 'Registration failed' });
      return { success: false, error: 'Registration failed' };
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      this.replaceState({ ...this.state, isLoading: true, error: null });
      const res = await rpcChangePassword(currentPassword, newPassword);
      if (!res.success) {
        this.replaceState({ ...this.state, isLoading: false, error: res.error || 'Password change failed' });
      } else {
        this.replaceState({ ...this.state, isLoading: false, error: null });
      }
      return res;
    } catch (error) {
      logger.error('[AuthController] changePassword error:', error);
      this.replaceState({ ...this.state, isLoading: false, error: 'Password change failed' });
      return { success: false, error: 'Password change failed' };
    }
  }

  clearError(): void {
    if (this.state.error) {
      this.replaceState({ ...this.state, error: null });
    }
  }

  /**
   * Проверяет, имеет ли текущий пользователь право на выполнение метода.
   * 
   * @param methodCode - код метода в формате module::Repository.method (например, 'accounting::AccountingMeasureRepository.count')
   * @returns Promise<boolean> - true, если пользователь имеет право на выполнение метода
   * 
   * @example
   * ```ts
   * const canCount = await authController.hasPermission('accounting::AccountingMeasureRepository.count');
   * ```
   */
  async hasPermission(methodCode: string): Promise<boolean> {
    try {
      if (!this.state.isAuthenticated || !this.state.user) {
        logger.debug('[AuthController] hasPermission: user not authenticated');
        return false;
      }
      
      const hasAccess = await rpcHasPermission(methodCode);
      logger.debug(`[AuthController] hasPermission(${methodCode}): ${hasAccess}`);
      return hasAccess;
    } catch (error) {
      logger.error('[AuthController] hasPermission error:', error);
      // В случае ошибки — отказываем в доступе (fail-secure)
      return false;
    }
  }
}

export default AuthController;


